import React from 'react';

const defaultOrder = ['personalInfo', 'summary', 'education', 'skills', 'professionalExperience', 'projects', 'certifications', 'awards'];

const MinimalTemplate = ({ data, layout, sectionVisibility, sectionOrder, theme }) => {
  const order = sectionOrder || defaultOrder;
  const visibleSections = order.filter(s => sectionVisibility[s] !== false);
  const isTwoColumn = layout === 'two-column';

  const fmtDate = (d) => d || '';

  const SectionTitle = ({ title }) => (
    <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
      {title}
    </div>
  );

  const renderSkills = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {data.skills.map((s, i) => (
        <span key={i} style={{
          color: theme.primaryColor || '#1e293b',
          fontSize: 13,
          fontWeight: 500
        }}>
          {s}{i < data.skills.length - 1 ? <span style={{ color: '#cbd5e1', marginLeft: 6 }}>/</span> : ''}
        </span>
      ))}
    </div>
  );

  const renderPersonalInfo = () => {
    const p = data.personalInfo;
    return (
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 400, color: theme.primaryColor || '#1e293b', letterSpacing: '-0.5px' }}>{p.fullName || 'Your Name'}</h2>
        {p.title && <div style={{ fontSize: 14, color: '#64748b', marginTop: 2, fontStyle: 'italic' }}>{p.title}</div>}
        <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: '2px 0' }}>
          {[p.email, p.phone, p.address].filter(Boolean).map((item, i, arr) => (
            <span key={i}>{item}{i < arr.length - 1 ? <span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span> : ''}</span>
          ))}
          {p.linkedin && <span><span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span> <a href={p.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: theme.accentColor || '#64748b', textDecoration: 'none' }}>LinkedIn</a></span>}
          {p.github && <span><span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span> <a href={p.github} target="_blank" rel="noopener noreferrer" style={{ color: theme.accentColor || '#64748b', textDecoration: 'none' }}>GitHub</a></span>}
        </div>
      </div>
    );
  };

  const renderSection = (key) => {
    switch (key) {
      case 'personalInfo':
        return renderPersonalInfo();
      case 'summary':
        return data.summary ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="About" />
            <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.8 }}>{data.summary}</div>
          </div>
        ) : null;
      case 'education':
        return data.education.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Education" />
            {data.education.map((edu, i) => (
              <div key={i} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 500, fontSize: 14, color: theme.primaryColor || '#1e293b' }}>{edu.school}</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>{edu.degree} — {fmtDate(edu.startDate)} - {fmtDate(edu.endDate) || 'Present'}</div>
                {edu.description && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{edu.description}</div>}
              </div>
            ))}
          </div>
        ) : null;
      case 'skills':
        return data.skills.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Skills" />
            {renderSkills()}
          </div>
        ) : null;
      case 'professionalExperience':
        return data.professionalExperience.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Experience" />
            {data.professionalExperience.map((exp, i) => (
              <div key={i} style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 500, fontSize: 14, color: theme.primaryColor || '#1e293b' }}>{exp.position}</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>{exp.company}{exp.location ? `, ${exp.location}` : ''} — {fmtDate(exp.startDate)} - {fmtDate(exp.endDate) || 'Present'}</div>
                {exp.description && <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, marginTop: 4 }}>{exp.description}</div>}
              </div>
            ))}
          </div>
        ) : null;
      case 'projects':
        return data.projects.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Projects" />
            {data.projects.map((proj, i) => (
              <div key={i} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 500, fontSize: 14, color: theme.primaryColor || '#1e293b' }}>{proj.title} <span style={{ fontWeight: 400, color: '#94a3b8' }}>— {fmtDate(proj.startDate)} - {fmtDate(proj.endDate) || 'Present'}</span></div>
                {proj.description && <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, marginTop: 2 }}>{proj.description}</div>}
                {proj.technologies && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{proj.technologies}</div>}
                {proj.link && <a href={proj.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: theme.accentColor || '#64748b', marginTop: 2, display: 'inline-block' }}>View Project →</a>}
              </div>
            ))}
          </div>
        ) : null;
      case 'certifications':
        return data.certifications.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Certifications" />
            {data.certifications.map((cert, i) => (
              <div key={i} style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>
                <span style={{ color: theme.primaryColor || '#1e293b', fontWeight: 500 }}>{cert.name}</span> — {cert.issuer}{cert.date ? ` (${fmtDate(cert.date)})` : ''}
              </div>
            ))}
          </div>
        ) : null;
      case 'awards':
        return data.awards.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Awards" />
            {data.awards.map((award, i) => (
              <div key={i} style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>
                <span style={{ color: theme.primaryColor || '#1e293b', fontWeight: 500 }}>{award.title}</span> — {award.issuer}{award.date ? ` (${fmtDate(award.date)})` : ''}
                {award.description && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>{award.description}</div>}
              </div>
            ))}
          </div>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: 48, fontFamily: theme.font || 'Georgia, serif', fontSize: theme.fontSize || '14px', lineHeight: theme.lineHeight || '1.8', color: '#334155', maxWidth: 680, margin: '0 auto' }}>
      {isTwoColumn ? (
        <div style={{ display: 'flex', gap: 32, flexDirection: 'row' }}>
          <div style={{ flex: '0 0 280px' }}>
            {visibleSections.slice(0, Math.ceil(visibleSections.length / 2)).map(s => (
              <div key={s}>{renderSection(s)}</div>
            ))}
          </div>
          <div style={{ flex: 1 }}>
            {visibleSections.slice(Math.ceil(visibleSections.length / 2)).map(s => (
              <div key={s}>{renderSection(s)}</div>
            ))}
          </div>
        </div>
      ) : (
        visibleSections.map(s => <div key={s}>{renderSection(s)}</div>)
      )}
    </div>
  );
};

export default MinimalTemplate;
